/**
 * Sanitising an operator-supplied brand mark, and an operator-supplied avatar.
 *
 * A brand mark is either a small inline picture or inline SVG. The picture
 * path is a bounded `data:` URI and there is nothing in it to sanitise; the
 * markup path is the rest of this file.
 *
 * Inline SVG is markup that the console puts on every screen, and SVG is a
 * full document format: it can carry `<script>`,
 * `on*` handlers, `<foreignObject>` with arbitrary HTML inside it, `<use>`
 * pointing at a remote document, and `href="javascript:..."`. Any one of those
 * turns "paste your logo" into "run your code in every operator's session".
 *
 * So this is an allowlist, not a blocklist. Only elements and attributes named
 * here survive; everything else is dropped, including anything invented after
 * this file was written. That is the whole point of choosing an allowlist: the
 * failure mode of a blocklist is silence, and the failure mode of an allowlist
 * is a logo that renders slightly wrong and gets reported.
 *
 * The parse is a hand-written scanner rather than a DOM, because the API runs
 * in Node with no DOM and pulling one in to read a 2 KB logo is a dependency
 * this stack does not have. The scanner is deliberately strict: anything it
 * cannot confidently understand is rejected outright rather than passed
 * through, so a construction it fails to model cannot become a hole.
 */
import { ValidationFailed } from './errors'

/** Bounded to match the check constraint on both columns. */
export const MARK_MAX_BYTES = 65_536

/**
 * Byte length, measured the same way Postgres will measure it.
 *
 * `TextEncoder` rather than `Buffer` so this module carries no Node built-in
 * and the console can import it: the browser has to make the same
 * raster-or-markup call the server makes, and a component that re-derives that
 * from a prefix is a component that eventually disagrees.
 */
function utf8Bytes(value: string): number {
  return new TextEncoder().encode(value).length
}

/**
 * Shape and paint only. No `<script>`, no `<style>` (which can load fonts and
 * carry `url()`), no `<image>` (which fetches), no `<use>` (which can reference
 * an external document), no `<foreignObject>` (which is HTML in disguise), no
 * `<animate>` family (which can set arbitrary attributes over time).
 */
const ALLOWED_ELEMENTS = new Set([
  'svg',
  'g',
  'path',
  'circle',
  'ellipse',
  'rect',
  'line',
  'polyline',
  'polygon',
  'defs',
  'linearGradient',
  'radialGradient',
  'stop',
  'title',
  'desc',
])

/**
 * Attributes that describe geometry and paint. Nothing here can name a URL, so
 * a sanitised mark cannot make a request, which is what keeps a logo from being
 * a tracking pixel for whoever supplied it.
 *
 * `class` and `style` are both absent on purpose. `style` carries `url()` and
 * can escape its element, and `class` lets a mark borrow console styles and
 * reposition itself over other controls.
 */
const ALLOWED_ATTRIBUTES = new Set([
  'viewbox',
  'width',
  'height',
  'x',
  'y',
  'x1',
  'x2',
  'y1',
  'y2',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'd',
  'points',
  'fill',
  'fill-rule',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-dasharray',
  'stroke-opacity',
  'opacity',
  'transform',
  'offset',
  'stop-color',
  'stop-opacity',
  'gradientunits',
  'gradienttransform',
  'id',
  'aria-hidden',
  'xmlns',
])

/**
 * A value that could name a resource or a script, whatever attribute it is on.
 * Checked in addition to the attribute allowlist, because `fill` legitimately
 * accepts `url(#id)` for a gradient and that is one character away from
 * `url(https://...)`.
 */
function valueIsSafe(name: string, value: string): boolean {
  const lower = value.trim().toLowerCase()
  if (lower.includes('javascript:') || lower.includes('data:') || lower.includes('&#')) return false
  if (lower.includes('url(')) {
    // Only a same-document gradient reference, which is what `url(#a)` is.
    return /^url\(\s*#[a-z0-9_-]+\s*\)$/i.test(lower)
  }
  if (name === 'fill' || name === 'stroke' || name === 'stop-color') {
    // Named colours, hex, rgb/hsl, `none`, `currentColor`. Anything else is a
    // construction this file does not model, so it does not survive.
    return /^(none|transparent|currentcolor|#[0-9a-f]{3,8}|[a-z]+|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\))$/i.test(lower)
  }
  return true
}

export interface SanitiseResult {
  svg: string
  /** What was removed, so the operator is told rather than left guessing. */
  removed: string[]
}

/**
 * A brand mark that is a picture rather than markup.
 *
 * Most real logos are not hand-written vectors. They arrive as a PNG exported
 * from whatever drew them, and three of the four brands on this platform are
 * exactly that: a rendered mark with soft edges and a hundred colours, which
 * nothing traces into `<path d="...">` without destroying it. Refusing them
 * would mean the console shows a monogram for the companies that have a logo,
 * which is the wrong outcome for a screen whose only job is saying which brand
 * you are looking at.
 *
 * So a mark may also be a raster `data:` URI, on the same terms the avatar
 * column already accepts one: inline, bounded, and never a remote URL. Note
 * what is *not* in this list. `image/svg+xml` is absent on purpose: an SVG
 * behind a `data:` URI is markup that never met the allowlist below, so
 * allowing it here would be a hole shaped exactly like the thing this file
 * exists to close. A vector logo goes through the markup path or not at all.
 */
const RASTER_MARK = /^data:image\/(png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/=]+$/i

/**
 * Whether a stored mark is a picture, and so must be drawn with `<img>` rather
 * than set as markup. Exported because the console has to make the same call
 * this module made, and inferring it from a prefix in a component is how the
 * two drift apart.
 */
export function isRasterMark(mark: string): boolean {
  return RASTER_MARK.test(mark.trim())
}

/**
 * Returns the mark with everything unrecognised stripped, plus a list of what
 * went. Throws only when there is nothing usable left, because a logo that
 * silently becomes an empty square is worse than a refusal.
 *
 * A raster mark passes through whole: there is nothing inside a base64 PNG for
 * an allowlist to act on, and the bound plus the strict shape above are the
 * entirety of what can be checked about one.
 */
export function sanitiseBrandMark(input: string): SanitiseResult {
  const raw = input.trim()
  if (raw === '') throw new ValidationFailed('A brand mark cannot be empty', [{ path: 'logoSvg', message: 'paste the SVG markup, or upload a picture' }])
  if (utf8Bytes(raw) > MARK_MAX_BYTES) {
    throw new ValidationFailed('That brand mark is too large', [
      { path: 'logoSvg', message: `keep it under ${Math.floor(MARK_MAX_BYTES / 1024)} KB, which is far more than any icon needs` },
    ])
  }
  if (isRasterMark(raw)) return { svg: raw, removed: [] }
  // Said before the `<svg` check, because "it has to start with an <svg> tag"
  // is a baffling answer to someone who just pasted a picture of their logo.
  if (/^data:/i.test(raw)) {
    throw new ValidationFailed('That kind of picture cannot be a brand mark', [
      { path: 'logoSvg', message: 'upload a PNG, JPEG, GIF or WebP, or paste the SVG markup itself rather than a data: link to it' },
    ])
  }
  if (!/^<svg[\s>]/i.test(raw)) {
    throw new ValidationFailed('That does not look like an SVG', [
      { path: 'logoSvg', message: 'it has to start with an <svg> tag' },
    ])
  }

  const removed = new Set<string>()
  let out = ''
  let index = 0
  /**
   * The elements we dropped whole, innermost last, so their children go with
   * them. A plain depth counter is wrong here: `<foreignObject>` exists to
   * carry HTML, HTML has void elements, and `<img ...>` written without a
   * closing slash increments a counter that nothing ever decrements. The mark
   * then loses every shape that followed the foreignObject, which is the whole
   * logo. Matching end tags by name and popping through to them survives that.
   */
  const skipStack: string[] = []

  while (index < raw.length) {
    const open = raw.indexOf('<', index)
    if (open === -1) {
      if (skipStack.length === 0) out += escapeText(raw.slice(index))
      break
    }
    if (skipStack.length === 0) out += escapeText(raw.slice(index, open))

    // A `<` that no tag name follows is text, not markup, which is the same
    // call the HTML parser makes. Without this, `<title>a < b</title>` reads
    // the stray `<` as an element, swallows the rest of the document looking
    // for its end tag, and returns an SVG missing its own closing tag.
    const next = raw[open + 1] ?? ''
    if (!/[a-zA-Z/!?]/.test(next)) {
      if (skipStack.length === 0) out += '&lt;'
      index = open + 1
      continue
    }

    // Comments, CDATA and doctypes carry no paint and are dropped whole.
    if (raw.startsWith('<!--', open)) {
      const end = raw.indexOf('-->', open)
      index = end === -1 ? raw.length : end + 3
      continue
    }
    if (raw.startsWith('<!', open) || raw.startsWith('<?', open)) {
      const end = raw.indexOf('>', open)
      index = end === -1 ? raw.length : end + 1
      continue
    }

    const close = findTagEnd(raw, open)
    if (close === -1) {
      throw new ValidationFailed('That SVG is malformed', [
        { path: 'logoSvg', message: 'a tag is never closed, so it cannot be read safely' },
      ])
    }
    const tag = raw.slice(open + 1, close)
    const isEnd = tag.startsWith('/')
    const body = isEnd ? tag.slice(1) : tag
    const selfClosing = body.trimEnd().endsWith('/')
    const nameMatch = /^([a-zA-Z][a-zA-Z0-9:-]*)/.exec(body.trim())
    const name = nameMatch?.[1] ?? ''
    const lowerName = name.toLowerCase()
    const canonical = canonicalElementName(lowerName)

    if (canonical === null) {
      removed.add(`<${name || '?'}>`)
      if (isEnd) popSkip(skipStack, lowerName)
      else if (!selfClosing) skipStack.push(lowerName)
      index = close + 1
      continue
    }

    if (skipStack.length > 0) {
      // Inside a dropped element. An allowed element nested in a dropped one is
      // still dropped, because whatever dropped its parent may have been what
      // gave it meaning.
      if (isEnd) popSkip(skipStack, lowerName)
      else if (!selfClosing) skipStack.push(lowerName)
      index = close + 1
      continue
    }

    if (isEnd) {
      out += `</${canonical}>`
      index = close + 1
      continue
    }

    const attrs = readAttributes(body.slice(name.length), removed, canonical)
    out += `<${canonical}${attrs}${selfClosing ? '/>' : '>'}`
    index = close + 1
  }

  const cleaned = out.trim()
  if (!/^<svg[\s>]/i.test(cleaned) || !/<(path|circle|ellipse|rect|line|polyline|polygon)\b/i.test(cleaned)) {
    throw new ValidationFailed('Nothing usable was left of that mark', [
      { path: 'logoSvg', message: 'after removing everything that could run or fetch, no shape remained' },
    ])
  }

  return { svg: cleaned, removed: [...removed].sort() }
}

/** SVG element names are case sensitive, so the allowlist match is folded and the real spelling restored. */
const CANONICAL: Record<string, string> = Object.fromEntries(
  [...ALLOWED_ELEMENTS].map((element) => [element.toLowerCase(), element]),
)

function canonicalElementName(lower: string): string | null {
  return CANONICAL[lower] ?? null
}

/**
 * Close the innermost dropped element with this name, and everything opened
 * inside it. An end tag naming nothing on the stack is stray markup and closes
 * nothing, which is the case that keeps a malformed paste from re-enabling
 * output halfway through a `<script>` body.
 */
function popSkip(stack: string[], name: string): void {
  const at = stack.lastIndexOf(name)
  if (at === -1) return
  stack.length = at
}

/**
 * The same restoration for attributes. `viewBox` written as `viewbox` still
 * renders when the mark is inlined into HTML, because the HTML parser carries a
 * fix-up table for exactly these names, but it stops rendering the moment the
 * markup is served as `image/svg+xml` or opened as XML. Emitting the spelling
 * the format actually defines costs one lookup and removes that whole class of
 * "it worked in the console and broke everywhere else".
 */
const CANONICAL_ATTRIBUTE: Record<string, string> = {
  viewbox: 'viewBox',
  gradientunits: 'gradientUnits',
  gradienttransform: 'gradientTransform',
}

function readAttributes(source: string, removed: Set<string>, element: string): string {
  let out = ''
  const pattern = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/g
  let match: RegExpExecArray | null
  while ((match = pattern.exec(source)) !== null) {
    const name = (match[1] ?? '').toLowerCase()
    const value = match[3] ?? match[4] ?? match[5] ?? ''
    if (!ALLOWED_ATTRIBUTES.has(name) || !valueIsSafe(name, value)) {
      removed.add(`${element}[${name}]`)
      continue
    }
    out += ` ${CANONICAL_ATTRIBUTE[name] ?? name}="${escapeAttribute(value)}"`
  }
  // A bare attribute with no value cannot carry paint and is dropped silently
  // only when it is on the allowlist; anything else is reported.
  const bare = source.replace(pattern, ' ')
  for (const token of bare.split(/\s+/)) {
    const lower = token.trim().replace(/\/$/, '').toLowerCase()
    if (lower !== '' && !ALLOWED_ATTRIBUTES.has(lower)) removed.add(`${element}[${lower}]`)
  }
  return out
}

/** Scans past quoted values so a `>` inside an attribute does not end the tag early. */
function findTagEnd(raw: string, open: number): number {
  let quote: string | null = null
  for (let i = open + 1; i < raw.length; i += 1) {
    const char = raw[i]
    if (quote !== null) {
      if (char === quote) quote = null
      continue
    }
    if (char === '"' || char === "'") quote = char
    else if (char === '>') return i
  }
  return -1
}

function escapeText(text: string): string {
  return text.replace(/[<>&]/g, (char) => (char === '<' ? '&lt;' : char === '>' ? '&gt;' : '&amp;'))
}

function escapeAttribute(value: string): string {
  return value.replace(/[<>&"]/g, (char) =>
    char === '<' ? '&lt;' : char === '>' ? '&gt;' : char === '&' ? '&amp;' : '&quot;',
  )
}

// ---------------------------------------------------------------------------
// Avatars
// ---------------------------------------------------------------------------

/**
 * An avatar is a URL or a data URI, never markup, so the rules are simpler:
 * it has to be https or an image data URI, and it has to fit the column.
 *
 * `http:` is refused because the console is https and a mixed-content image is
 * a broken image on every screen rather than a picture. `data:image/svg+xml` is
 * refused too: an SVG data URI in an `<img>` is not scriptable in a modern
 * browser, but it is one browser bug away from being so, and nobody needs their
 * avatar to be a vector.
 */
export function normaliseAvatarUrl(input: string): string {
  const value = input.trim()
  if (value === '') throw new ValidationFailed('A picture is required', [{ path: 'avatarUrl', message: 'paste a link or upload a file' }])
  if (utf8Bytes(value) > MARK_MAX_BYTES) {
    throw new ValidationFailed('That picture is too large', [
      { path: 'avatarUrl', message: `keep it under ${Math.floor(MARK_MAX_BYTES / 1024)} KB, which is a generous size for a small round picture` },
    ])
  }
  if (/^https:\/\/[^\s"'<>]+$/i.test(value)) return value
  if (/^data:image\/(png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/=]+$/i.test(value)) return value
  throw new ValidationFailed('That picture cannot be used', [
    { path: 'avatarUrl', message: 'use an https link, or upload a PNG, JPEG, GIF or WebP' },
  ])
}
